// Kit 01 · items 23, 24, 25.
//
//   23 Toggle switch   track 36 by 20 · knob 16 · travel 16 · duration fast
//   24 Checkbox        box 16 · radius 4 · check stroke 2.25 · label gap 12
//   25 Radio group     circle 16 · dot 6 · label gap 12 · row gap 12
//
// keys: Space toggles a checkbox or a switch. In a radio group, arrows move
//       AND select, and the group is one tab stop.
//
// All three are <button> with an aria role rather than a styled native input.
// A native checkbox cannot paint an indeterminate glyph, and the two would
// have to be kept in sync by hand — which is the defect this kit is full of
// warnings about.

import { useRef } from "react";
import { Check, Minus } from "lucide-react";
import { Icon } from "./icon";
import { cx, stateAttr } from "./kit-state";
import type { KitState, Stateful } from "./kit-state";

/* ── 23 · toggle switch ─────────────────────────────────────────────────── */

export interface ToggleProps extends Stateful {
  checked: boolean;
  onChange?: (next: boolean) => void;
  label: string;
  /** Hide the visible label but keep the accessible one. */
  labelHidden?: boolean;
  className?: string;
}

export function Toggle({ checked, onChange, label, labelHidden, state, className }: ToggleProps) {
  const on = state === "on" ? true : state === "off" ? false : checked;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={labelHidden ? label : undefined}
      className={cx("k-toggle", className)}
      disabled={state === "disabled"}
      onClick={() => onChange?.(!on)}
      {...stateAttr(state)}
    >
      <span className="k-toggle__track" aria-hidden="true">
        <span className="k-toggle__knob" />
      </span>
      {labelHidden ? null : <span className="k-toggle__label">{label}</span>}
    </button>
  );
}

/* ── 24 · checkbox ──────────────────────────────────────────────────────────
   Three values, not two. `indeterminate` is a real state of a parent whose
   children disagree, and it renders a dash rather than a tick — never an
   empty box, which would claim the children all said no. */

export type CheckedState = boolean | "mixed";

export interface CheckboxProps extends Stateful {
  checked: CheckedState;
  onChange?: (next: boolean) => void;
  label: string;
  /** Hide the visible label but keep the accessible one. Same prop, same
      meaning and same implementation as item 23's toggle: a select-all column
      in a table has a checkbox per row and no room for a label beside it, and
      the alternative to this is an unnamed control. Added in Phase 6 by the
      screen that first needed it; the label is still REQUIRED. */
  labelHidden?: boolean;
  className?: string;
  invalid?: boolean;
}

export function Checkbox({ checked, onChange, label, labelHidden, state, className, invalid }: CheckboxProps) {
  const value: CheckedState =
    state === "checked" ? true
    : state === "indeterminate" ? "mixed"
    : checked;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={value === "mixed" ? "mixed" : value}
      aria-label={labelHidden ? label : undefined}
      aria-invalid={invalid || state === "error" || undefined}
      className={cx("k-choice", className)}
      disabled={state === "disabled"}
      onClick={() => onChange?.(value !== true)}
      {...stateAttr(state)}
    >
      <span className="k-choice__box" aria-hidden="true">
        {value === "mixed" ? <Icon icon={Minus} size="sm" /> : value ? <Icon icon={Check} size="sm" /> : null}
      </span>
      {labelHidden ? null : <span className="k-choice__label">{label}</span>}
    </button>
  );
}

/* ── 25 · radio group ───────────────────────────────────────────────────────
   One tab stop for the group; arrows move and select together, because a
   radio group where focus and selection can separate is a group where a
   keyboard user can leave without choosing anything. */

export interface RadioOption {
  value: string;
  /** Usually one string. A ReactNode so an option can carry a second, muted
   *  line under the primary one, which is what a choice between real properties
   *  needs: the name a person recognises on top and the identifier under it. */
  label: React.ReactNode;
  disabled?: boolean;
  state?: KitState;
}

export interface RadioGroupProps extends Stateful {
  options: RadioOption[];
  value: string;
  onChange?: (value: string) => void;
  label: string;
  className?: string;
}

export function RadioGroup({ options, value, onChange, label, state, className }: RadioGroupProps) {
  const group = useRef<HTMLDivElement | null>(null);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const usable = options.filter((o) => !o.disabled);
    if (usable.length === 0) return;
    const at = usable.findIndex((o) => o.value === value);
    let next = at;
    if (event.key === "ArrowDown" || event.key === "ArrowRight") next = (at + 1) % usable.length;
    else if (event.key === "ArrowUp" || event.key === "ArrowLeft") next = (at - 1 + usable.length) % usable.length;
    else return;
    event.preventDefault();
    const target = usable[next];
    onChange?.(target.value);
    group.current?.querySelector<HTMLButtonElement>(`[data-value="${target.value}"]`)?.focus();
  }

  return (
    <div
      ref={group}
      role="radiogroup"
      aria-label={label}
      className={cx("k-radio-group", className)}
      onKeyDown={onKeyDown}
    >
      {options.map((option) => {
        const checked = option.value === value;
        const rowState = option.state ?? (state === "disabled" ? "disabled" : undefined);
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            data-value={option.value}
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            className="k-choice k-choice--radio"
            disabled={option.disabled || state === "disabled"}
            onClick={() => onChange?.(option.value)}
            {...stateAttr(rowState ?? (checked ? "checked" : undefined))}
          >
            <span className="k-choice__box" aria-hidden="true">
              {checked ? <span className="k-choice__dot" /> : null}
            </span>
            <span className="k-choice__label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
